import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import OrganizationOnboardingModal from '../onboarding/OrganizationOnboardingModal';
import ForceChangePasswordModal from './ForceChangePasswordModal';

const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false, studentOnly = false }) => {
  const { user, isAuthenticated, isLoading, needsOnboarding, completeOnboarding, dismissOnboarding, mustChangePassword, clearMustChangePassword } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Platform-admin (superuser) only routes — e.g. EasyLaw → firm subscription
  // billing. Matches the backend `IsPlatformAdmin` gate exactly (the serializer
  // now exposes `is_superuser`). Org-level admins/owners must NOT reach these.
  if (superAdminOnly && !user?.is_superuser) {
    return <Navigate to="/admin" replace />;
  }

  // If admin-only route and user is not admin, redirect to dashboard
  if (adminOnly && user?.role !== 'admin' && user?.role !== 'owner' && !user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  // Student-only routes — non-students (and non-superusers) bounce to the dashboard.
  if (studentOnly && user?.role !== 'student' && !user?.is_superuser) {
    return <Navigate to="/" replace />;
  }

  // Show onboarding modal if user needs to set up their organization
  return (
    <>
      {mustChangePassword && (
        <ForceChangePasswordModal onComplete={clearMustChangePassword} />
      )}
      {needsOnboarding && (
        <OrganizationOnboardingModal
          user={user}
          onComplete={completeOnboarding}
          onSkip={dismissOnboarding}
        />
      )}
      {children}
    </>
  );
};

export default ProtectedRoute;
